import { TeethSelect } from "@jasper/forms";
import { Stack } from "@mantine/core";
import { useForm } from "@tanstack/react-form";
import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import { useProductGroup } from "../../hooks/useProductGroup";
import { usePreselectedTeeth } from "../../hooks/usePreselectedTeeth";
import type { ProductStepProps, TeethMutationStrategy } from "../types";
import {
  useTeethSelectionSubmission,
  type FormatTeethSelection,
} from "./useTeethSelectionSubmission";
import { useRegisterStepSave } from "../useRegisterStepSave";
import { useTeethSelectionDescription } from "./useTeethSelectionDescription";

type TeethValue = {
  teeth: number[];
  groupedTeeth: number[][];
};

export type TeethStepValidatorInput = {
  value: TeethValue;
  setTeethInError: Dispatch<SetStateAction<number[]>>;
};

type TeethStepValidator = (
  input: TeethStepValidatorInput
) => Record<string, unknown> | undefined;

type GenericTeethStepProps = ProductStepProps & {
  validateSelection: TeethStepValidator;
  groupAdjacentByDefault?: boolean;
  applyTeethSelection: FormatTeethSelection<number>;
  mutationStrategy?: TeethMutationStrategy;
  validateOnChange?: boolean;
  hideGroupAllButtons?: boolean;
  getTeethDescription?: (args: {
    teeth: number[];
    groupedTeeth?: number[][];
    descriptionLevel: "global" | "group";
  }) => { badgeContent: string; description: string };
  solidarizedLabel?: string;
};

export const GenericTeethStep = ({
  stepKey,
  products,
  orderConfiguration,
  validateSelection,
  groupAdjacentByDefault = false,
  applyTeethSelection,
  mutationStrategy,
  validateOnChange,
  registerStepSave,
  itemType,
  hideGroupAllButtons,
  getTeethDescription,
  solidarizedLabel,
}: GenericTeethStepProps) => {
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.teeth",
  });
  const describeTeethSelection = useTeethSelectionDescription();

  const { groupTeeth, groupProducts } = useProductGroup(products);
  const preselectedTeeth = usePreselectedTeeth();

  const initialTeeth = useMemo(
    () => (preselectedTeeth.length ? preselectedTeeth : groupTeeth),
    [preselectedTeeth, groupTeeth]
  );

  const initialGroupedTeeth = useMemo(
    () => groupProducts.map(groupProduct => groupProduct.teeth),
    [groupProducts]
  );

  // Teeth of the invalid groups only, so that the valid ones are not shown in error.
  const [teethInError, setTeethInError] = useState<number[]>([]);

  const submitTeethSelection = useTeethSelectionSubmission({
    products,
    itemType,
    orderConfiguration,
    formatTeethSelection: applyTeethSelection,
    mutationStrategy,
  });

  const defaultValues = useMemo(
    () => ({
      product: {
        teeth: [...initialTeeth],
        groupedTeeth: [...initialGroupedTeeth],
      },
    }),
    [initialTeeth, initialGroupedTeeth]
  );

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      await submitTeethSelection(value.product.groupedTeeth);
    },
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  const handleToothClick = ({
    toothId,
    selectedTeeth,
    setSelectedTeeth,
  }: {
    toothId: number;
    selectedTeeth: number[];
    setSelectedTeeth: (teeth: number[]) => void;
  }) => {
    if (selectedTeeth.includes(toothId)) {
      setSelectedTeeth(selectedTeeth.filter(id => id !== toothId));
    } else {
      setSelectedTeeth([...selectedTeeth, toothId]);
    }
  };

  return (
    <Stack gap="md" flex={1} mih={0} h="100%" p="md">
      <form.Field
        name="product"
        listeners={{
          onChange: ({ fieldApi }) => {
            fieldApi.setErrorMap({
              onSubmit: undefined,
              onBlur: undefined,
            });
          },
        }}
        validators={{
          onChange: ({ value, fieldApi }) => {
            const res = validateSelection({
              value,
              setTeethInError,
            });

            if (!res) {
              fieldApi.setErrorMap({
                onBlur: undefined,
                onSubmit: undefined,
              });
            }

            return res;
          },

          onBlur: ({ value }) =>
            validateSelection({
              value,
              setTeethInError,
            }),
          onSubmit: ({ value }) =>
            validateSelection({
              value,
              setTeethInError,
            }),
        }}
      >
        {field => {
          const formErrors =
            field.state.meta.errorMap.onBlur ??
            field.state.meta.errorMap.onSubmit ??
            (validateOnChange ? field.state.meta.errorMap.onChange : undefined);

          const errors =
            typeof formErrors?.teeth === "string"
              ? t(formErrors?.teeth, {
                  keyPrefix: "steps.teeth.errors",
                  defaultValue: t("errorOccurred", { keyPrefix: "common" }),
                })
              : undefined;

          const getTeethInError = () => {
            if (!errors) {
              return [];
            }

            if (teethInError.length) {
              return teethInError;
            }

            return field.state.value.teeth;
          };

          return (
            <Stack>
              <TeethSelect
                onBlur={() => field.handleBlur()}
                teeth={field.state.value.teeth}
                groupedTeeth={field.state.value.groupedTeeth}
                error={errors}
                teethError={getTeethInError()}
                withGrouping
                hideGroupAllButtons={hideGroupAllButtons}
                groupAdjacentByDefault={groupAdjacentByDefault}
                solidarizedLabel={solidarizedLabel}
                onClick={(toothId, newGroups) => {
                  setTeethInError([]);
                  handleToothClick({
                    toothId,
                    selectedTeeth: field.state.value.teeth,
                    setSelectedTeeth: teeth =>
                      field.setValue({
                        ...field.state.value,
                        teeth,
                        groupedTeeth: newGroups,
                      }),
                  });
                }}
                onGroupedTeethChange={newGroups => {
                  setTeethInError([]);
                  field.setValue({
                    ...field.state.value,
                    groupedTeeth: newGroups,
                  });
                }}
                getTeethDescription={
                  getTeethDescription ??
                  (({ teeth }) =>
                    describeTeethSelection(teeth, [
                      {
                        type: itemType,
                        count: teeth.length,
                      },
                    ]))
                }
              />
            </Stack>
          );
        }}
      </form.Field>
    </Stack>
  );
};
